/**
 * ConfigSection - Tournament configuration for Americano
 */
import React, { useState, useEffect } from 'react';
import { useTournament } from '../../contexts/TournamentContext';
import { useAuth } from '../../contexts/AuthContext';
import firebaseSyncService from '../../services/firebaseSync';

const ConfigSection = () => {
  const { state, setConfig } = useTournament();
  const { isSignedIn, currentUser } = useAuth();
  const savedConfig = state.config;

  const [formData, setFormData] = useState({ ...savedConfig });
  const defaultName = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `Americano du ${dd}/${mm}/${yyyy}`;
  };
  const [tournamentName, setTournamentName] = useState(defaultName());
  const [rawPlayerCount, setRawPlayerCount] = useState(String(savedConfig.playerCount ?? ''));
  const [rawCourtCount, setRawCourtCount] = useState(String(savedConfig.courtCount ?? ''));
  // Sync from context (e.g. after Firebase load)
  useEffect(() => {
    setFormData({ ...savedConfig });
    setRawPlayerCount(String(savedConfig.playerCount ?? ''));
    setRawCourtCount(String(savedConfig.courtCount ?? ''));
  }, [savedConfig]);

  useEffect(() => {
    if (!currentUser) return;
    const id = firebaseSyncService.currentTournamentId || localStorage.getItem('currentTournamentId');
    if (!id) return;

    if (firebaseSyncService.currentTournamentName) {
      setTournamentName(firebaseSyncService.currentTournamentName);
    } else {
      firebaseSyncService.getTournamentName(id, currentUser.email).then((name) => {
        if (name) setTournamentName(name);
        else setTournamentName(defaultName());
      });
    }
  }, [currentUser, state.players]);

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };

    // Auto-calculate roundCount from playerCount
    if (field === 'playerCount') {
      updated.roundCount = value - 1;
    }

    // Keep courtNames in sync with courtCount
    if (field === 'courtCount') {
      const count = parseInt(value) || 1;
      const existing = updated.courtNames || [];
      if (count > existing.length) {
        for (let i = existing.length; i < count; i++) {
          existing.push(`Terrain ${i + 1}`);
        }
      }
      updated.courtNames = existing.slice(0, count);
    }

    setFormData(updated);
    setConfig(updated);
  };

  useEffect(() => {
    if (!tournamentName.trim()) return;
    const id = firebaseSyncService.currentTournamentId || localStorage.getItem('currentTournamentId');
    if (!isSignedIn() || !currentUser || !id) return;

    const timeout = setTimeout(async () => {
      try {
        await firebaseSyncService.renameTournament(id, tournamentName.trim(), currentUser.email);
      } catch (err) {
        console.warn('Could not save tournament name:', err);
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [tournamentName, isSignedIn, currentUser]);

  // Calculate total time: (joueurs - 1) × (match + pause) + échauffement
  const rounds = formData.roundCount || 0;
  const totalCalc =
    (formData.playerCount - 1) * ((formData.matchDuration || 0) + (formData.pauseDuration || 0)) +
    (formData.warmupDuration || 0);

  return (
    <div className="section-inner config-section">
      <div className="section-header">
        <h1><i className="bi bi-gear-fill me-3"></i>Configuration</h1>
      </div>

      <div className="section-body">
        <div className="material-form">
          <div className="config-form-layout">

            {/* Nom du tournoi */}
            <div className="form-input-material">
              <label htmlFor="tournamentName">🏆 Nom du tournoi</label>
              <input
                type="text"
                id="tournamentName"
                value={tournamentName}
                onChange={(e) => setTournamentName(e.target.value)}
              />
            </div>

            {/* Joueurs + Terrains */}
            <div className="config-grid-2">
              <div className="form-input-material">
                <label htmlFor="playerCount">👥 Joueurs</label>
                <input
                  type="number"
                  id="playerCount"
                  value={rawPlayerCount}
                  onChange={(e) => setRawPlayerCount(e.target.value)}
                  onBlur={(e) => {
                    const val = Math.max(4, Math.min(64, parseInt(e.target.value) || 4));
                    setRawPlayerCount(String(val));
                    handleChange('playerCount', val);
                  }}
                  min={4} max={64} step={4}
                />
              </div>
              <div className="form-input-material">
                <label htmlFor="courtCount">🏟️ Terrains</label>
                <input
                  type="number"
                  id="courtCount"
                  value={rawCourtCount}
                  onChange={(e) => setRawCourtCount(e.target.value)}
                  onBlur={(e) => {
                    const val = Math.max(1, Math.min(16, parseInt(e.target.value) || 1));
                    setRawCourtCount(String(val));
                    handleChange('courtCount', val);
                  }}
                  min={1} max={16}
                />
              </div>
            </div>

            {/* Points */}
            <div className="form-input-material">
              <label htmlFor="pointsPerMatch">🎯 Points / match</label>
              <select
                id="pointsPerMatch"
                value={formData.pointsPerMatch}
                onChange={(e) => handleChange('pointsPerMatch', parseInt(e.target.value))}
              >
                <option value={16}>16 points</option>
                <option value={24}>24 points</option>
                <option value={32}>32 points</option>
              </select>
            </div>

            {/* Heure de début */}
            <div className="form-input-material">
              <label htmlFor="startTimeStr">🕐 Heure de début</label>
              <input
                type="time"
                id="startTimeStr"
                value={formData.startTimeStr}
                onChange={(e) => handleChange('startTimeStr', e.target.value)}
              />
            </div>

            {/* Durées */}
            <p className="config-group-label">Durées</p>
            <div className="config-grid-3">
              <div className="form-input-material">
                <label htmlFor="warmupDuration">🏃 Échauff.</label>
                <input
                  type="number"
                  id="warmupDuration"
                  value={formData.warmupDuration}
                  onChange={(e) => handleChange('warmupDuration', parseInt(e.target.value) || 0)}
                  min={0} max={60}
                />
              </div>
              <div className="form-input-material">
                <label htmlFor="pauseDuration">☕ Pause</label>
                <input
                  type="number"
                  id="pauseDuration"
                  value={formData.pauseDuration}
                  onChange={(e) => handleChange('pauseDuration', parseInt(e.target.value) || 0)}
                  min={0} max={30}
                />
              </div>
              <div className="form-input-material">
                <label htmlFor="matchDuration">🎾 Match</label>
                <input
                  type="number"
                  id="matchDuration"
                  value={formData.matchDuration}
                  onChange={(e) => handleChange('matchDuration', parseInt(e.target.value) || 10)}
                  min={5} max={120}
                />
              </div>
            </div>

            {/* Résumé */}
            <div className="config-time-summary">
              <span>{rounds} tours × {formData.matchDuration} min match</span>
              {formData.pauseDuration > 0 && (
                <span> + {rounds} × {formData.pauseDuration} min pause</span>
              )}
              {formData.warmupDuration > 0 && (
                <span> + {formData.warmupDuration} min échauffement</span>
              )}
              <span className="config-time-total"> = {totalCalc} min</span>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigSection;
